import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, Pressable, Platform, useWindowDimensions } from 'react-native';
import { ChevronLeft, Info, Menu, Table2, Trophy } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { useAvatarContext } from '../../../src/avatars/contexts/AvatarContext';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { OrgContextChip } from '../../../src/accounts/components/OrgSwitcher/OrgContextChip';
import { OrgSwitcherSheet } from '../../../src/accounts/components/OrgSwitcher/OrgSwitcherSheet';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { resolveCompetitionCrest } from '../../../src/competitions/helpers/competitionCrest.helper';
import { competitionHeaderTitle } from '../../../src/competitions/helpers/competitionHeaderTitle.helper';
import { CompetitionCrest } from '../CompetitionCrest/CompetitionCrest';
// Same style factory as the WC header so both shells share identical chrome.
import { getWorldCupScreenHeaderStyles } from '../../worldcup/WorldCupScreenHeader/WorldCupScreenHeader.styles';
import {
  COMPETITION_HEADER_AVATAR_SIZE,
  COMPETITION_HEADER_ICON_SIZE,
  COMPETITION_HEADER_ICON_SIZE_NARROW,
  COMPETITION_HEADER_CREST_SIZE,
  COMPETITION_HEADER_CREST_SIZE_NARROW,
  COMPETITION_HEADER_CREST_INSET,
  COMPETITION_HEADER_SHOW_INFO_MIN_WIDTH,
  COMPETITION_HEADER_COPY,
  COMPETITION_HEADER_TEST_IDS,
} from './CompetitionScreenHeader.constants';

export interface CompetitionScreenHeaderProps {
  title: string;
  subtitle?: string | null;
  /** League crest from GET /competitions; falls back to a trophy glyph. */
  logoUrl?: string | null;
  /** `brand.flagCode` — states the composed flag instead of inferring it from the title. */
  flagCode?: string | null;
  /** Back to the tournaments hub — takes the slot the WC header gives Standings. */
  onBackPress: () => void;
  onRulesPress?: () => void;
  /**
   * Opens the Table | Bracket modal. Absent when the competition has neither, which is what
   * keeps a season pool's header exactly as it was (specs/champions-league Stories 5 & 6).
   * Takes the slot the World Cup header gives its own standings button, for the same reason:
   * reference material belongs in the header, not in the tab bar next to Picks.
   */
  onStandingsPress?: () => void;
  onMenuPress: () => void;
  testID?: string;
}

/**
 * Competition shell header (specs/competitions-hub-client Slice 3).
 *
 * Mirrors WorldCupScreenHeader's layout and chrome, with competition-driven content.
 * A separate component by design: the WC header is the reference implementation and
 * stays untouched (it owns the standings modal + WC rules store wiring).
 */
export const CompetitionScreenHeader: React.FC<CompetitionScreenHeaderProps> = ({
  title,
  subtitle,
  logoUrl,
  flagCode,
  onBackPress,
  onRulesPress,
  onStandingsPress,
  onMenuPress,
  testID = COMPETITION_HEADER_TEST_IDS.root,
}) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const { width } = useWindowDimensions();
  const isNarrow = Platform.OS !== 'web' || width < 768;
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext();

  const styles = useMemo(
    () => getWorldCupScreenHeaderStyles(theme, themeMode, isNarrow),
    [theme, themeMode, isNarrow],
  );

  const headerIconColor = themeMode === 'normal' ? theme.white : theme.primary;
  const iconSize = isNarrow ? COMPETITION_HEADER_ICON_SIZE_NARROW : COMPETITION_HEADER_ICON_SIZE;
  const crestSize = isNarrow ? COMPETITION_HEADER_CREST_SIZE_NARROW : COMPETITION_HEADER_CREST_SIZE;
  // Resolved here so a competition with no crest AND no flag keeps the header's trophy glyph.
  const crest = useMemo(
    () => resolveCompetitionCrest({ logoUrl, name: title, flagCode }),
    [logoUrl, title, flagCode],
  );
  // The season badge below already shows "26/27"; the brand name carries it too, so drop it there.
  const titleText = useMemo(() => competitionHeaderTitle(title, subtitle), [title, subtitle]);
  const showInfoButton = (!isNarrow || width >= COMPETITION_HEADER_SHOW_INFO_MIN_WIDTH)
    && onRulesPress != null;

  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const handleAvatarPress = useCallback(() => setIsSwitcherOpen(true), []);
  const handleSwitcherClose = useCallback(() => setIsSwitcherOpen(false), []);

  const sideButtonStyle = ({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
    styles.headerSideButton,
    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
    pressed && styles.headerSideButtonPressed,
    hovered && Platform.OS === 'web' && styles.headerSideButtonHover,
  ];

  return (
    <View style={styles.screenHeader} testID={testID}>
      <View style={styles.screenHeaderLeadFlex}>
        <OrgContextChip testID="competition-header-org-context-chip" />
        {isNarrow && (
          <Pressable
            onPress={handleAvatarPress}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={({ pressed, hovered }) => [
              styles.headerSideButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.headerSideButtonPressed,
              hovered && Platform.OS === 'web' && styles.headerSideButtonHover,
            ]}
            testID="competition-header-avatar"
            accessibilityRole="button"
            accessibilityLabel="Switch account"
          >
            <FriendAvatar
              avatarId={(currentAvatarId ?? null) as DisplayAvatarId | null}
              avatarConfig={currentAvatarConfig}
              size={COMPETITION_HEADER_AVATAR_SIZE}
              nameForInitials=""
              small
            />
          </Pressable>
        )}
        {/* Always mounted: unmounting a closing Modal skips its onDismiss (see OrgSwitcherSheet). */}
        <OrgSwitcherSheet visible={isSwitcherOpen} onClose={handleSwitcherClose} />
        <Pressable
          style={sideButtonStyle}
          onPress={onBackPress}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          testID={COMPETITION_HEADER_TEST_IDS.back}
          accessibilityRole="button"
          accessibilityLabel={COMPETITION_HEADER_COPY.BACK_ACCESSIBILITY}
        >
          <ChevronLeft size={iconSize} color={headerIconColor} strokeWidth={2.2} />
        </Pressable>
      </View>

      <View style={styles.screenHeaderTitleCluster}>
        <View style={styles.screenHeaderIcon}>
          {crest.kind === 'monogram' ? (
            // A competition with neither a crest nor a flag keeps the plain trophy glyph
            // it has always had here — three letters on a white plate would read as a bug.
            <Trophy size={crestSize} color={headerIconColor} strokeWidth={2.2} />
          ) : (
            <CompetitionCrest
              logoUrl={logoUrl}
              name={title}
              flagCode={flagCode}
              size={crestSize + COMPETITION_HEADER_CREST_INSET * 2}
              logoSize={crestSize}
              testID={COMPETITION_HEADER_TEST_IDS.crest}
            />
          )}
        </View>
        <View style={styles.screenHeaderDivider} />
        <Text style={styles.screenTitleText} numberOfLines={1} ellipsizeMode="tail">
          {titleText}
        </Text>
        {subtitle ? (
          <Text style={styles.screenTitleYear} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </View>

      <View style={styles.screenHeaderTrailFlex}>
        {onStandingsPress != null && (
          <Pressable
            style={sideButtonStyle}
            onPress={onStandingsPress}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            testID={COMPETITION_HEADER_TEST_IDS.standings}
            accessibilityRole="button"
            accessibilityLabel={COMPETITION_HEADER_COPY.STANDINGS_ACCESSIBILITY}
          >
            <Table2 size={iconSize} color={headerIconColor} strokeWidth={2.2} />
          </Pressable>
        )}
        {showInfoButton && (
          <Pressable
            style={sideButtonStyle}
            onPress={onRulesPress}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            testID={COMPETITION_HEADER_TEST_IDS.rules}
            accessibilityRole="button"
            accessibilityLabel={COMPETITION_HEADER_COPY.RULES_ACCESSIBILITY}
          >
            <Info size={iconSize} color={headerIconColor} strokeWidth={2.2} />
          </Pressable>
        )}
        <Pressable
          style={sideButtonStyle}
          onPress={onMenuPress}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          testID={COMPETITION_HEADER_TEST_IDS.menu}
          accessibilityRole="button"
          accessibilityLabel={COMPETITION_HEADER_COPY.MENU_ACCESSIBILITY}
        >
          <Menu size={iconSize} color={headerIconColor} strokeWidth={2.2} />
        </Pressable>
      </View>
    </View>
  );
};
